import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {EditComponent} from 'web-shared';
import {
  ApiResponse, ClassificatorsQuery,
  ClassificatorValueQuery,
  ClassificatorValuesQuery, CONSTANTS,
  DateHelpers,
  GLOBAL,
  LeModalService, UsersQuery
} from 'common-shared';
import {ItemGroupQuery} from 'web-shared';
import {AbstractControl, NgForm} from '@angular/forms';
import {IntegratedMasterService} from '../../integrated-master-editor/services/integrated-master.service';
import {UnitService} from 'web-shared';
import {ItemInItemGroupModel, ManufacturingItemModel} from 'web-shared';
import {ManufacturingItemService} from '../manufacturing-item.service';
import {uniqueCodeValidator} from '../validators';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {StateService} from 'web-shared';
import {map, takeUntil} from 'rxjs/operators';
import {ManufacturingItemGroupService} from '../../manufactured-item-groups/manufacturing-item-group.service';
import {NgbNavChangeEvent} from "@ng-bootstrap/ng-bootstrap";
import {
  ManufacturingItemEditModalComponent
} from "../manufacturing-item-edit-modal/manufacturing-item-edit-modal.component";
import {CopyPasteService} from "web-shared";
import {IAppSettings} from "web-shared";
import {SpecLinesEditComponent} from "../../../common/spec/spec-lines-edit/spec-lines-edit.component";

@Component({
  selector: 'mes-manufacturing-item-edit',
  templateUrl: './manufacturing-item-edit.component.html',
  styles: []
})
export class ManufacturingItemEditComponent extends EditComponent implements OnInit, OnChanges {
  @Input() onChange: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  @Input() item: ManufacturingItemModel;
  @Input() selectedTab: string;
  @Input() response: ApiResponse<any>;
  @Input() scrollIntoViewId: string;
  @Input() shouldResetSaveState$: Subject<boolean>;
  @Input() wasValidated$: Subject<boolean> = new Subject<boolean>();
  @Input() readonly: boolean;
  itemGroups: ItemGroupQuery[];
  @ViewChild(NgForm, {static: true}) form: NgForm;
  autoReplenishFlags: ClassificatorValueQuery[];
  @Output() setTab = new EventEmitter<string>();
  @Output() changed = new EventEmitter<boolean>();
  settings: IAppSettings;
  showPropertyLocalizations: boolean = false;
  @ViewChild(SpecLinesEditComponent, {static: false}) specLinesEditComponent: SpecLinesEditComponent;
  global = GLOBAL;
  modifiedBy: string;
  DateHelpers = DateHelpers;
  statusCls$ = new Observable<string[]>();

  constructor(public classificatorValuesQuery: ClassificatorValuesQuery, public manufacturingItemService: ManufacturingItemService, public groupService: ManufacturingItemGroupService, private integratedMasterService: IntegratedMasterService, public unitService: UnitService, private stateService: StateService, public usersQuery: UsersQuery, private modalService: LeModalService, public copyPasteService: CopyPasteService, public classificatorsQuery: ClassificatorsQuery) {
    super();
  }

  ngOnInit() {
    this.selectedTab ??= 'general';
    this.stateService.settings$.subscribe(value => {
      this.settings = value;
    });

    this.form.statusChanges.pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      this.changed.emit(this.form.pristine);
      this.addAsyncValidatorIfNotExist(this.form.controls.code);
    });

    this.classificatorValuesQuery.autoReplenishFlags$.pipe(takeUntil(this.componentDestroyed)).subscribe(value => this.autoReplenishFlags = value);
    this.groupService.getAll$.pipe(takeUntil(this.componentDestroyed)).subscribe(value => this.itemGroups = value);
    this.shouldResetSaveState$.pipe(takeUntil(this.componentDestroyed)).subscribe(() => this.setFormPristine());
  }

  setFormPristine(): void {
    Object.keys(this.form.controls).forEach(control => {
      this.form.controls[control].markAsPristine();
    });
    this.changed.emit(this.form.pristine);
  }

  addAsyncValidatorIfNotExist(codeFormControl: AbstractControl) {
    if (codeFormControl && !codeFormControl.asyncValidator) {
      codeFormControl.setAsyncValidators(uniqueCodeValidator(this.manufacturingItemService.getAll$, this.item));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.selectedTab || changes.item?.currentValue) && this.selectedTab === 'ime' && this.item) {
      this.ensureImeLoaded();
    }
    if (changes.item?.currentValue) {
      this.generateModifiedMessage();
    }
  }

  ensureImeLoaded(force = false) {
    if ((!this.item.isImeLoaded() && !this.item.SaveRouting) || force) {
      this.integratedMasterService.getAll(undefined, this.item.Id).subscribe(value => {
        this.integratedMasterService.loadIme(this.item, value);
        this.onChange.next(value);
      });
    }
  }

  setChanged() {
    this.form.form.markAsDirty();
    this.changed.emit(this.form.pristine);
  }

  selectTab($event: NgbNavChangeEvent) {
    if ($event?.nextId) {
      this.setTab.emit($event.nextId);
    }
  }

  initialize(item: ManufacturingItemModel) {
    this.item = item;
    if (this.item.SaveRouting) {
      this.ensureImeLoaded(true);
    }
    this.onChange.next('general');
  }

  removeConnection(entity: ItemInItemGroupModel) {
    this.item.ItemInItemGroups = [...this.item.ItemInItemGroups.filter(value => value !== entity)];
    this.setChanged();
  }

  setDateRange(dateRange: { From: Date, To: Date }, entity: ItemInItemGroupModel) {
    if (entity.FromDate !== dateRange?.From || entity.ToDate !== dateRange?.To) {
      this.setChanged();
    }
    entity.FromDate = dateRange ? dateRange.From : null;
    entity.ToDate = dateRange ? dateRange.To : null;
  }

  private generateModifiedMessage() {
    if (this.item.ModifiedBy === GLOBAL.Users.Admin) {
      this.modifiedBy = `Modified on ${DateHelpers.formatDate(this.item.ModifiedDate, 'dd.MM.yyyy HH:mm:ss')}`;
    } else if (this.item.ModifiedBy) {
      this.usersQuery.get$(this.item.ModifiedBy).pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
        if (value) {
          const modifier = value.Fullname ?? value.Email;
          this.modifiedBy = this.item.ModifiedDate > new Date(2000, 1, 1)
            ? `Modified by ${modifier} on ${DateHelpers.formatDate(this.item.ModifiedDate, 'dd.MM.yyyy HH:mm:ss')}`
            : DateHelpers.equals(this.item.ModifiedDate, this.item.ModifiedDateForItemGroup)
              ? `Modified by ${modifier}`
              : `Modified by ${modifier}. Item and item group modified dates are different.`;
        }
      });
    }

    this.statusCls$ = this.classificatorsQuery.getWithParentIds(this.item.StatusCl).pipe(map(values => {
      if (!values?.length) {
        return [CONSTANTS.ItemStatus.ClassificatorId]
      }
      return values;
    }));
  }

  openDraftFinalCounterpart() {
    this.modalService.show(ManufacturingItemEditModalComponent, {initialState: {entityId: this.item.ParentId ?? this.item.DraftId}, class: 'modal-xl'});
  }
}
